import { z } from 'zod';
import { MoneySchema } from '../common';
import { JobLocation } from './hiring.enums';
import { EventCrewAssignmentState } from './crew-assignment.enums';

/** External-hire schedule. Omit all three to keep current (event-linked) behavior. */
const ExternalHireScheduleFields = {
  /** Wall-clock date + time in `timezone` (e.g. 2026-09-20T18:00:00). */
  scheduledStartLocal: z.iso.datetime({ local: true, offset: false }).optional(),
  /** IANA timezone for `scheduledStartLocal` (e.g. Africa/Lagos). */
  timezone: z.string().trim().min(1).max(100).optional(),
  /** Gig length in minutes. */
  durationMinutes: z.coerce.number().int().min(5).max(720).optional(),
};

const refineExternalHireScheduleTogether = (
  data: {
    scheduledStartLocal?: string | undefined;
    timezone?: string | undefined;
    durationMinutes?: number | undefined;
  },
  ctx: z.RefinementCtx,
) => {
  const fields = {
    scheduledStartLocal: data.scheduledStartLocal !== undefined,
    timezone: data.timezone !== undefined,
    durationMinutes: data.durationMinutes !== undefined,
  };
  const provided = Object.values(fields).filter(Boolean).length;
  if (provided > 0 && provided < 3) {
    const missing = (Object.keys(fields) as Array<keyof typeof fields>).filter(
      (key) => !fields[key],
    );
    ctx.addIssue({
      code: 'custom',
      message:
        'timezone, scheduledStartLocal, and durationMinutes must be provided together ' +
        `(missing: ${missing.join(', ')}). Partial schedule edits are not supported; send all three fields.`,
      path: ['scheduledStartLocal'],
    });
  }
};

const RoleFieldsSchema = z.object({
  role: z.string().trim().min(1).max(120),
  responsibilities: z.string().trim().min(1).max(5000),
  requirements: z.string().trim().min(1).max(5000),
  compensation: MoneySchema,
  location: z.enum(JobLocation),
  notes: z.string().trim().max(2000).optional(),
  ...ExternalHireScheduleFields,
});

/** `POST /host/events/:eventId/crew-assignments` */
export const CreateEventCrewAssignmentRequestSchema = RoleFieldsSchema.extend({
  vendorId: z.uuid(),
}).superRefine(refineExternalHireScheduleTogether);

/**
 * `POST /host/crew-assignments/external` — Direct Hire with no LivDot event.
 * Title + full schedule are required (there is no event to inherit from).
 */
export const CreateExternalEventCrewAssignmentRequestSchema = RoleFieldsSchema.extend({
  vendorId: z.uuid(),
  /** Display name for the off-platform / external gig. */
  title: z.string().trim().min(1).max(200),
  scheduledStartLocal: z.iso.datetime({ local: true, offset: false }),
  timezone: z.string().trim().min(1).max(100),
  durationMinutes: z.coerce.number().int().min(5).max(720),
});

/** `PATCH /host/events/:eventId/crew-assignments/:id` */
export const UpdateEventCrewAssignmentRequestSchema = RoleFieldsSchema.partial()
  .refine((body) => Object.keys(body).length > 0, {
    message: 'At least one field is required',
  })
  .superRefine(refineExternalHireScheduleTogether);

/** `POST /host/events/:eventId/crew-assignments/:id/replace` */
export const ReplaceEventCrewAssignmentRequestSchema = z
  .object({
    vendorId: z.uuid(),
    role: z.string().trim().min(1).max(120).optional(),
    responsibilities: z.string().trim().min(1).max(5000).optional(),
    requirements: z.string().trim().min(1).max(5000).optional(),
    compensation: MoneySchema.optional(),
    location: z.enum(JobLocation).optional(),
    notes: z.string().trim().max(2000).optional(),
    ...ExternalHireScheduleFields,
  })
  .superRefine(refineExternalHireScheduleTogether);

export const EventCrewAssignmentVendorSummarySchema = z.object({
  id: z.uuid(),
  companyName: z.string(),
  displayName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  vendorIsVerified: z.boolean(),
});

export const EventCrewAssignmentResponseSchema = z.object({
  id: z.uuid(),
  /** Null for external Direct Hire (no LivDot event). */
  eventId: z.uuid().nullable(),
  hostId: z.uuid(),
  vendorId: z.uuid(),
  role: z.string(),
  responsibilities: z.string(),
  requirements: z.string(),
  compensation: MoneySchema,
  location: z.enum(JobLocation),
  notes: z.string().nullable(),
  state: z.enum(EventCrewAssignmentState),
  bookingId: z.uuid().nullable(),
  invitedAt: z.iso.datetime().nullable(),
  respondedAt: z.iso.datetime().nullable(),
  rejectReason: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  vendor: EventCrewAssignmentVendorSummarySchema.optional(),
  /** LivDot event title, or the external-hire `title` when eventId is null. */
  eventTitle: z.string().optional(),
  eventScheduledStartLocal: z.iso.datetime({ local: true, offset: false }).nullable().optional(),
  /** Set when the invite used an external-hire schedule; otherwise null. */
  scheduledStartLocal: z.iso.datetime({ local: true, offset: false }).nullable().optional(),
  timezone: z.string().nullable().optional(),
  durationMinutes: z.number().int().nullable().optional(),
});

export const ListEventCrewAssignmentsResponseSchema = z.object({
  items: z.array(EventCrewAssignmentResponseSchema),
});

/**
 * `GET /host/events/:eventId/crew-assignments`
 * Optional Manage Production Team filters.
 *
 * Tab mapping (FE):
 * - All → omit `state`
 * - Confirmed → `state=ACCEPTED`
 * - Pending → `state=INVITED` or `state=INVITED,DRAFT`
 * - Cancelled → `state=CANCELLED` or `state=CANCELLED,REJECTED`
 */
export const ListEventCrewAssignmentsQuerySchema = z.object({
  /** Case-insensitive match on vendor display name or company name. */
  search: z.string().trim().min(1).max(100).optional(),
  /** Case-insensitive exact match on assignment role. */
  role: z.string().trim().min(1).max(120).optional(),
  /**
   * Invitation status filter (`EventCrewAssignmentState`).
   * Accepts one value or comma-separated values (e.g. `INVITED,DRAFT`).
   */
  state: z.preprocess(
    (raw) => {
      if (raw == null || raw === '') return undefined;
      if (Array.isArray(raw)) {
        return raw.map((v) => String(v).trim()).filter(Boolean);
      }
      return String(raw)
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean);
    },
    z.array(z.enum(EventCrewAssignmentState)).min(1).optional(),
  ),
});

/** `POST /marketplace/crew-assignments/:id/reject` */
export const RejectEventCrewAssignmentRequestSchema = z.object({
  reason: z.string().trim().min(5).max(500),
});

export const AcceptEventCrewAssignmentResponseSchema = z.object({
  assignment: EventCrewAssignmentResponseSchema,
  bookingId: z.uuid(),
});

export const ReplaceEventCrewAssignmentResponseSchema = z.object({
  assignment: EventCrewAssignmentResponseSchema,
  replacedBookingId: z.uuid(),
});
